/*
 * Caret-aware mention parsing for the composer editor.
 * Canonical token format: "@type: display name".
 */

export interface ActiveMention {
  /** Index of the "@" that opens the token. */
  start: number
  stage: 'type' | 'search'
  typeToken: string
  query: string
}

/**
 * Returns the mention token the caret is currently inside (from its "@" up to the
 * caret), or null. Works anywhere in the text — the user may click into a token
 * mid-message and keep typing.
 */
export const getMentionAtCaret = (
  text: string,
  caret: number,
  isCompletedDisplay: (_segment: string) => boolean
): ActiveMention | null => {
  const before = text.slice(0, caret)
  const at = before.lastIndexOf('@')
  if (at < 0) {
    return null
  }
  // "@" must start a word (start of text or after whitespace)
  if (at > 0 && !/\s/.test(text[at - 1])) {
    return null
  }

  const token = before.slice(at + 1)
  if (token.includes('\n')) {
    return null
  }
  // Caret sits right after an already-completed mention → nothing active
  if (isCompletedDisplay(before.slice(at))) {
    return null
  }

  const colon = token.indexOf(':')
  if (colon === -1) {
    // Type stage: "@spe" — letters/underscore only
    if (!/^[a-zA-Z_]{0,30}$/.test(token)) {
      return null
    }
    return { start: at, stage: 'type', typeToken: token, query: token }
  }

  const typeToken = token.slice(0, colon)
  if (!/^[a-zA-Z_]{1,30}$/.test(typeToken)) {
    return null
  }

  const query = token.slice(colon + 1).replace(/^ /, '')
  // Runaway guard: long free text after an abandoned token is not a search anymore
  if (query.length > 50 || query.split(/\s+/).filter(Boolean).length > 6) {
    return null
  }

  return { start: at, stage: 'search', typeToken, query }
}
